import React, { useLayoutEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { gsap } from "gsap";

import watch from "../assets/watch-ultra.png";
import buds from "../assets/buds-pro.png";
import samsung from "../assets/samsungs22ultra.png";
import iphone from "../assets/iphone17.png";

import { Swiper, SwiperSlide } from "swiper/react";
import SwiperCore, { Pagination, Autoplay, Navigation } from "swiper";
import "swiper/swiper-bundle.min.css";
import "swiper/swiper.min.css";

SwiperCore.use([Pagination, Navigation, Autoplay]);

function HomeBanner() {
  const heroRef = useRef(null);

  useLayoutEffect(() => {
    if (!heroRef.current) {
      return undefined;
    }

    const visual = heroRef.current.querySelector(".hero-banner__visual");

    const ctx = gsap.context(() => {
      const timeline = gsap.timeline({
        defaults: {
          ease: "power3.out",
        },
      });

      timeline.from(".hero-banner", {
        opacity: 0,
        y: 26,
        duration: 0.7,
      });

      timeline.from(
        ".hero-banner__eyebrow, .hero-banner__copy h2, .hero-banner__copy p, .hero-banner__actions, .hero-banner__stats",
        {
          y: 42,
          opacity: 0,
          duration: 0.9,
          stagger: 0.1,
        },
        "-=0.35"
      );

      gsap.from(".hero-banner__slider, .hero-banner__aside > *", {
        y: 36,
        opacity: 0,
        duration: 1,
        stagger: 0.14,
        delay: 0.15,
        ease: "power3.out",
      });

      gsap.to(".hero-banner__glow", {
        x: 14,
        y: -10,
        scale: 1.05,
        duration: 5.5,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
      });

      gsap.to(".hero-banner__glow--secondary", {
        x: -18,
        y: 14,
        scale: 1.08,
        duration: 6.8,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
      });

      gsap.to(".hero-banner__slider img", {
        yPercent: -3,
        duration: 3.8,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
      });

      gsap.to(".hero-banner__mini", {
        y: (index) => (index === 0 ? -8 : 8),
        duration: 3.6,
        repeat: -1,
        yoyo: true,
        stagger: 0.2,
        ease: "sine.inOut",
      });
    }, heroRef);

    const handlePointerMove = (event) => {
      if (!visual || window.innerWidth <= 768) {
        return;
      }

      const bounds = visual.getBoundingClientRect();
      const relX = (event.clientX - bounds.left) / bounds.width - 0.5;
      const relY = (event.clientY - bounds.top) / bounds.height - 0.5;

      gsap.to(".hero-banner__slider", {
        x: relX * 22,
        y: relY * 18,
        rotationY: relX * 4,
        rotationX: relY * -4,
        transformPerspective: 1000,
        duration: 0.6,
        ease: "power3.out",
      });

      gsap.to(".hero-banner__aside", {
        x: relX * -14,
        y: relY * -10,
        duration: 0.7,
        ease: "power3.out",
      });

      gsap.to(".hero-banner__glow", {
        x: relX * 24,
        y: relY * 18,
        duration: 0.9,
        ease: "power3.out",
      });
    };

    const handlePointerLeave = () => {
      gsap.to(".hero-banner__slider, .hero-banner__aside, .hero-banner__glow", {
        x: 0,
        y: 0,
        rotationX: 0,
        rotationY: 0,
        duration: 0.8,
        ease: "power3.out",
      });
    };

    if (visual) {
      visual.addEventListener("pointermove", handlePointerMove);
      visual.addEventListener("pointerleave", handlePointerLeave);
    }

    return () => {
      if (visual) {
        visual.removeEventListener("pointermove", handlePointerMove);
        visual.removeEventListener("pointerleave", handlePointerLeave);
      }

      ctx.revert();
    };
  }, []);

  return (
    <section className="home-banner__container" ref={heroRef}>
      <div className="hidden-title">
        <h1>Buzz Mobile | Премиум мобилара</h1>
      </div>
      <div className="hero-banner container">
        <div className="hero-banner__copy">
          <span className="hero-banner__eyebrow">BuzzMobile</span>
          <h2>
            Премиум мобилно искуство. Твоето место за мобилна технологија.
          </h2>
          <p>
            Најнови телефони, внимателно избрани додатоци и професионален сервис
            – сè што ви треба за вашиот мобилен на едно место.
          </p>
          <div className="hero-banner__actions">
            <a
              href="#featured-phones"
              className="hero-banner__button hero-banner__button--solid"
            >
              Разгледај телефони
            </a>
            <Link
              to="/service"
              className="hero-banner__button hero-banner__button--ghost"
            >
              Сервис цени
            </Link>
          </div>
          <div className="hero-banner__stats">
            <div className="hero-banner__card">
              <strong>Избрани модели</strong>
              <span>Фокус на најпосакуваните телефони и премиум серии.</span>
            </div>
            <div className="hero-banner__card">
              <strong>Професионален сервис</strong>
              <span>Професионална поправка за сите видови смартфони.</span>
            </div>
          </div>
        </div>

        <div className="hero-banner__visual">
          <div className="hero-banner__glow" />
          <div className="hero-banner__glow hero-banner__glow--secondary" />

          <div className="hero-banner__slider hero-banner__card">
            <Swiper
              className="hero-banner__swiper"
              pagination={{ dynamicBullets: true }}
              modules={[Autoplay, Pagination, Navigation]}
              loop={true}
              navigation={true}
              // autoplay={{ delay: 3200, disableOnInteraction: false }}
            >
              <SwiperSlide>
                <Link to="/telefoni/Apple" className="hero-banner__slide-link">
                  <img src={iphone} alt="Apple iPhone" />
                </Link>
              </SwiperSlide>
              <SwiperSlide>
                <Link
                  to="/telefoni/Samsung"
                  className="hero-banner__slide-link"
                >
                  <img src={samsung} alt="Samsung телефон" />
                </Link>
              </SwiperSlide>
            </Swiper>
          </div>

          <div className="hero-banner__aside">
            <Link
              to="/telefon/apple-watch-ultra-2"
              className="hero-banner__mini hero-banner__card"
            >
              <img src={watch} alt="Apple Watch Ultra" />
              <div>
                <span>Смарт часовници</span>
                <p>карактер и моќен изглед.</p>
              </div>
            </Link>
            <Link
              to="/galanterija/slusalki"
              className="hero-banner__mini hero-banner__card"
            >
              <img src={buds} alt="Безжични слушалки" />
              <div>
                <span>Bluetooth слушалки</span>
                <p>Минимални форми, моќен звук и секојдневна удобност.</p>
              </div>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

export default HomeBanner;
